import { FormProvider } from 'react-hook-form';
import {
  applySpec,
  compose,
  find,
  pathOr,
  prop,
  propOr,
  toString
} from 'ramda';
import MembersInformations from './components/MembersInformations';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer';
import DownloadDocumentsContainer from '@@viewCommonComponents/displayer/DownloadDocumentsContainer';
import DisplaySelectedOffers from '@@viewCommonComponents/displayer/DisplaySelectedOffers';
import PropTypes from 'prop-types';
import { offerCodeEnum } from '@@viewEnums';
import GuaranteesDeathPrevTns from './components/GuaranteesDeathPrevTns.jsx';
import GuaranteesDisabilityPrevTns from './components/GuaranteesDisabilityPrevTns.jsx';
import {
  labelTotalAmountMonth,
  titleCotisation
} from './helpers/riskBasedContributionConfig.js';
import classNames from 'classnames';
import { getIsSelected } from '@@viewHelpers/getPropsGuarantees.js';
import getMessageInfo from '@@viewHelpers/getMessageInfo';
import ConfirmationModal from '@@viewCommonComponents/displayer/ConfirmationModal.jsx';
import useSimulationRecap from './hooks/useSimulationRecap';
import {
  DashedTitle,
  Icon,
  InfoMessage,
  Message,
  PageTitle,
  QuoteTable,
  TableCard
} from '@fasstech/middle-design-system';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import AdherentInformations from './components/AdherentInformations.jsx';
import { useTabs } from '@@viewCommonComponents/hooks/useTabs.js';
import React from 'react';
import SimulationRecapExtraForm from './extra/SimulationRecapExtraForm.jsx';
import displayExtraMessageCondition from './extra/displayExtraMessageCondition';
import { isCustomerModePavillon } from '@@viewCommonComponents/displayer/extra/isCustomerModePavillon';
import { isNilOrEmpty } from '@@serverHelpers';
import { CompanyInformations } from './components/FormulaRecap/CompanyInformations';
import { ControlledDatePicker } from '@@viewCommonComponents/controlled';
import { DemographyInformations } from './components/FormulaRecap/DemographyInformations';
import { PricingFormulasCollectiveHealth } from '../../_common/client/components/PricingFormulas/PricingFormulasCollectiveHealth';
import { getCollegeLabel } from './helpers/collegeHelper.js';

const SimulationRecapForm = ({
  getMiddleText,
  getMiddleBoolean,
  getSimulationRecap: project,
  metadata,
  regimeObligatoire = [],
  professionalSituations = [],
  jobs = [],
  socioProfessionalCategories = [],
  activities = [],
  phoneIndicatives = [],
  usersInformation = [],
  viewMode
}) => {
  const { grantees } = applySpec({
    grantees: propOr([], 'grantees'),
    customer: propOr([], 'customer')
  })(project);

  const customer = propOr({}, 'customer')(project);

  const { tabs, updateVisibility, isButtonDisabled, displayTabsPagination } =
    useTabs({
      grantees: [customer, ...grantees],
      getMiddleText,
      defaultMaxVisibleTabs: 2,
      filteredViewMode: !getMiddleBoolean(
        'BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE'
      ),
      keepFirstTabFixed: true
    });

  const {
    form,
    onSubmit,
    onModifyEffectDate,
    downloadMutation,
    sendEmailMutation,
    $onClickPrev,
    setDocumentsDownloaded,
    monthlyPrice,
    monthlyPriceMadelin,
    annualPrice,
    annualPriceMadelin,
    pricingLevel,
    isDutyOfAdviceSkipped,
    offerCode,
    guarantees,
    PTIADeathGuarantees,
    spousePensionGuarantees,
    educationPensionGuarantees,
    disabilityGuarantees,
    invalidityGuarantees,
    subscriptionStatus,
    numberPricings,
    signatureAdvice,
    isAdviceSignatureSentForSignature,
    isAdviceSignatureError,
    isAdviceSignatureSigned,
    displayConfirmationModal,
    setDisplayConfirmationModal,
    saveCustomerInfo,
    sendingSignature,
    cantGoNext,
    packs,
    isViewModeCustomer,
    currentIndex,
    setCurrentIndex,
    beneficiariesEnrollment,
    displayCommissionBlock,
    canSendMail,
    setCanSendMail
  } = useSimulationRecap({
    project,
    getMiddleBoolean,
    getMiddleText,
    customer,
    grantees,
    viewMode,
    usersInformation
  });

  const round = (value) => Math.round(value * 100) / 100;
  const formatPrice = compose(toString, round);

  const cotisationRows = [
    {
      label: labelTotalAmountMonth(offerCode),
      monthlyAmount: formatPrice(monthlyPrice),
      annualAmount: formatPrice(annualPrice),
      bold: true
    },
    ...(offerCode === offerCodeEnum.SELF_EMPLOYED_WORKER_PROTECTION
      ? [
        {
          label: 'Déductible Madelin',
          monthlyAmount: formatPrice(monthlyPriceMadelin) || '0',
          annualAmount: formatPrice(annualPriceMadelin) || '0',
          bold: false
        }
      ]
      : [])
  ];

  const commissionRows = [
    {
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_TYPE_LABEL'
      ),
      value: pathOr('', ['commission', 'type'], project)
    },
    {
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_LISTE_LABEL'
      ),
      value: pathOr('', ['commission', 'rate'], project)
    }
  ];

  const customerModePavillonResult = isCustomerModePavillon(isViewModeCustomer);

  const isCustomerMode =
    customerModePavillonResult === true ? false : isViewModeCustomer;

  const demography = propOr(null, 'demography')(project);
  const company = propOr(null, 'company')(project);

  return (
    <FormProvider {...form}>
      <PageLayout
        withFormLayout={false}
        className={'flex flex-col gap-4 justify-center items-center'}
      >
        <div className={'grid grid-cols-1 gap-8 w-full md:w-[90%]! '}>
          <div className={'flex flex-col items-center gap-4'}>
            <Icon name={'bag'} />
            <PageTitle>
              {getMiddleText('SYNTHESE_SIMULATION_VIEW_TITLE')}
            </PageTitle>
          </div>

          <DashedTitle className={'flex justify-center items-center'}>
            {getMiddleText(
              isViewModeCustomer
                ? 'SYNTHESE_SIMULATION_SUBSCRIBERS_TITLE'
                : 'SYNTHESE_SIMULATION_PRODUCTS_TITLE'
            )}
          </DashedTitle>
          {offerCode === offerCodeEnum.GROUP_HEALTH && (
            <div className="flex flex-row gap-4">
              <div className="w-3/8 mr-4">
                <ControlledDatePicker
                  form={form}
                  name="offer.effectDate"
                  disabled={true}
                  label={getMiddleText(
                    'PROCESSDEVENTE_EFFECT_DATE_PLACEHOLDER'
                  )}
                />
              </div>
              {!isNilOrEmpty(company) && (
                <CompanyInformations
                  company={{
                    name: prop('name', company),
                    siret: prop('siret', company),
                    ccn:
                      !prop('ccnId', company) ||
                      prop('ccnId', company) === '9999'
                        ? '-'
                        : `${prop('ccnId', company)} - ${prop('ccnName', company)}`
                  }}
                  customer={customer}
                />
              )}
              {!isNilOrEmpty(demography) && (
                <DemographyInformations
                  getMiddleBoolean={getMiddleBoolean}
                  demography={{
                    category: getCollegeLabel(demography.college),
                    averageAge: demography.employeeAverageAge,
                    employeeCount: demography.employeeCount,
                    childrenCount: demography.childrenCount,
                    partnerCount: demography.partnerCount,
                    customerSocialSecurityRegime: compose(
                      prop('label'),
                      find(
                        (regime) =>
                          regime.value ===
                          pathOr(
                            null,
                            ['socialSecurities', 0, 'regime'],
                            demography
                          )
                      ),
                      propOr([], 'regimes')
                    )(regimeObligatoire)
                  }}
                />
              )}
            </div>
          )}

          {(offerCode === offerCodeEnum.INDIVIDUAL_HEALTH ||
            offerCode ===
              offerCodeEnum.INDIVIDUAL_PROTECTION_DAILY_BENEFIT) && (
            <div className={'pt-4'}>
              <AdherentInformations
                customer={propOr({}, 'customer', project)}
                grantees={propOr([], 'grantees', project)}
                onModifyEffectDate={onModifyEffectDate}
                getMiddleText={getMiddleText}
                getMiddleBoolean={getMiddleBoolean}
                form={form}
                updateVisibility={updateVisibility}
                isButtonDisabled={isButtonDisabled}
                displayTabsPagination={displayTabsPagination}
                tabs={tabs.tabs}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
              />
            </div>
          )}

          {offerCode === offerCodeEnum.SELF_EMPLOYED_WORKER_PROTECTION && (
            // Prev tns
            <MembersInformations
              customer={propOr({}, 'customer', project)}
              grantees={propOr([], 'grantees', project)}
              onModifyEffectDate={onModifyEffectDate}
              getMiddleText={getMiddleText}
              getMiddleBoolean={getMiddleBoolean}
              metadata={metadata}
              regimeObligatoire={regimeObligatoire}
              professionalSituations={professionalSituations}
              activities={activities}
              jobs={jobs}
              socioProfessionalCategories={socioProfessionalCategories}
            />
          )}

          {offerCode === offerCodeEnum.INDIVIDUAL_HEALTH && (
            <DisplaySelectedOffers
              packs={packs}
              title={getMiddleText('SYNTHESE_SIMULATION_PRODUCTS_TITLE')}
              enableSelection={false}
              isViewModeCustomer={isCustomerMode}
              currentIndex={currentIndex}
              tabs={tabs.tabs}
            />
          )}
          {offerCode === offerCodeEnum.GROUP_HEALTH &&
            !isNilOrEmpty(project) && (
            <>
              <DashedTitle>
                {getMiddleText('NIVEAUXGARANTIESETTARIFS_GENERAL_VIEW_TITLE')}
              </DashedTitle>
              {
                <PricingFormulasCollectiveHealth
                  prices={project.prices}
                  demography={demography}
                  getMiddleText={getMiddleText}
                  employerPercentShare={prop(
                    'employerPercentShare',
                    demography
                  )}
                  formulaLevelLabel={project.formulaLevelLabel}
                  tariffStructureLabel={project.tariffStructureLabel}
                  beneficiariesEnrollment={beneficiariesEnrollment}
                />
              }
            </>
          )}

          <DashedTitle
            isDisplayed={getMiddleBoolean(
              'NIVEAUXGARANTIESETTARIFS_GARANTIES_TARIF_EST_DEMANDE_NIVEAUX_GARANTIE_TARIF'
            )}
          >
            {getMiddleText('SYNTHESE_SIMULATION_PRODUCTS_TITLE')}
          </DashedTitle>
        </div>

        <div
          className={
            'grid grid-cols-1 md:grid-cols-2! gap-8 w-full md:w-[90%]!'
          }
        >
          <div
            className={classNames('w-full', {
              hidden:
                !getIsSelected('OPTION_TT_CAUSE', guarantees) &&
                !getIsSelected('OPTION_ACC', guarantees) &&
                !getIsSelected('OPTION_REDUC', guarantees) &&
                !getIsSelected('OPTION_RCONJ', guarantees)
            })}
          >
            {getMiddleBoolean(
              'NIVEAUXGARANTIESETTARIFS_GAR_DECES_EST_NECESSAIRE'
            ) && (
              <>
                {(getIsSelected('OPTION_TT_CAUSE', guarantees) ||
                  getIsSelected('OPTION_ACC', guarantees) ||
                  getIsSelected('OPTION_REDUC', guarantees) ||
                  getIsSelected('OPTION_RCONJ', guarantees)) && (
                  <GuaranteesDeathPrevTns
                    getMiddleText={getMiddleText}
                    getMiddleBoolean={getMiddleBoolean}
                    guarantees={guarantees}
                    PTIADeathGuarantees={PTIADeathGuarantees}
                    spousePensionGuarantees={spousePensionGuarantees}
                    educationPensionGuarantees={educationPensionGuarantees}
                  />
                )}
              </>
            )}
          </div>

          <div
            className={classNames('w-full', {
              hidden:
                !getIsSelected('OPTION_INCAP', guarantees) &&
                !getIsSelected('OPTION_INVAL', guarantees)
            })}
          >
            {(getIsSelected('OPTION_INCAP', guarantees) ||
              getIsSelected('OPTION_INVAL', guarantees)) && (
              <GuaranteesDisabilityPrevTns
                getMiddleText={getMiddleText}
                getMiddleBoolean={getMiddleBoolean}
                guarantees={guarantees}
                disabilityGuarantees={disabilityGuarantees}
                invalidityGuarantees={invalidityGuarantees}
              />
            )}
          </div>

          <div className={'grow w-full grid col-span-1 md:col-span-2 gap-y-4'}>
            <QuoteTable
              title={`Garantie ${getMiddleText('GENERAL_RISQUE')}`}
              iconName={'hospital'}
              size={30}
              rowTitle={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_GARANTIES_CHOIX_NIV_GAR_LABEL'
              )}
              showColumnsTitle={false}
              rows={[
                {
                  label: getMiddleText(
                    'NIVEAUXGARANTIESETTARIFS_GARANTIES_CHOIX_NIV_GAR_LABEL'
                  ),
                  annualAmount: formatPrice(pricingLevel)
                }
              ]}
              isDisplayed={
                offerCode ===
                  offerCodeEnum.INDIVIDUAL_PROTECTION_DAILY_BENEFIT &&
                isDutyOfAdviceSkipped
              }
            />
            <div className={'grid grid-cols-3 gap-4'}>
              <QuoteTable
                title={titleCotisation(offerCode)}
                iconName={'calculatorFilled'}
                size={30}
                rowTitle={getMiddleText(
                  'NIVEAUXGARANTIESETTARIFS_GARANTIES_CHOIX_NIV_GAR_LABEL'
                )}
                rows={cotisationRows}
                className={`${displayCommissionBlock ? 'col-span-2' : 'col-span-3'} border p-4 rounded-lg`}
                isDisplayed={getMiddleBoolean(
                  'NIVEAUXGARANTIESETTARIFS_GARANTIES_TARIF_EST_DEMANDE_NIVEAUX_GARANTIE_TARIF'
                )}
              />
              <TableCard
                title={getMiddleText(
                  'NIVEAUXGARANTIESETTARIFS_TAUX_COMMISSION_TITLE'
                )}
                iconName={'euro'}
                className={'col-span-1 border-[#E7E7E7]'}
                rows={commissionRows}
                isDisplayed={displayCommissionBlock}
              />
            </div>
            {displayExtraMessageCondition(packs) && (
              <InfoMessage iconName={'lightbulb'}>
                {getMessageInfo(
                  getMiddleText('SYNTHESE_MESSAGE_INFO'),
                  subscriptionStatus
                )}
              </InfoMessage>
            )}
            <SimulationRecapExtraForm form={form} offerCode={offerCode} />
          </div>
        </div>
        <DownloadDocumentsContainer
          downloadMutation={downloadMutation}
          documentsList={propOr([], 'documentsToDownload', project)}
          sendEmailMutation={sendEmailMutation}
          userEmail={pathOr('', ['user', 'emails', 0, 'value'], project)}
          customerEmail={pathOr(
            '',
            ['customer', 'emails', 0, 'value'],
            project
          )}
          customerCivility={pathOr('', ['customer', 'civility'], project)}
          customerLastName={pathOr('', ['customer', 'lastName'], project)}
          userFirstName={pathOr('', ['user', 'firstName'], project)}
          userLastName={pathOr('', ['user', 'lastName'], project)}
          setDocumentsDownloaded={setDocumentsDownloaded}
          getMiddleText={getMiddleText}
          setCanSendMail={setCanSendMail}
          canSendMail={canSendMail}
        />
        <InfoMessage iconName={'lightbulb'}>
          {getMiddleText(
            'SYNTHESE_SIMULATION_DOWNLOAD_SEND_DOCUMENTS_MESSAGE_INFO'
          )}
        </InfoMessage>
      </PageLayout>

      <div className={'my-4'}>
        <ButtonContainer
          $onPreviousButtonClick={$onClickPrev}
          $onNextButtonClick={onSubmit}
          previousButtonIsDisabled={
            signatureAdvice &&
            !isAdviceSignatureError &&
            isAdviceSignatureSentForSignature
          }
          nextButtonLabel={
            signatureAdvice && !isAdviceSignatureSigned
              ? 'Signer électroniquement document'
              : numberPricings < 2
                ? getMiddleText('GENERAL_COMMON_TEXTS_SUBSCRIBE_BUTTON_LABEL')
                : 'Choisir la formule à souscrire'
          }
          displayResumeButton
          nextButtonIsDisabled={cantGoNext}
          nextButtonIsLoading={sendingSignature}
          nextButtonIsHidden={isAdviceSignatureError}
          getMiddleText={getMiddleText}
        />

        <InfoMessage isDisplayed={isAdviceSignatureSentForSignature}>
          {getMiddleText('PROCESSDEVENTE_SIGNATURE_SYNTHESE_MESSAGE_INFO')}
        </InfoMessage>

        {isAdviceSignatureError && (
          <Message
            variant="error"
            label={getMiddleText(
              'PROCESSDEVENTE_SIGNATURE_SYNTHESE_MESSAGE_BLOCAGE'
            )}
          />
        )}

        <ConfirmationModal
          isDisplayed={displayConfirmationModal}
          confirm={saveCustomerInfo}
          close={() => setDisplayConfirmationModal(false)}
          getMiddleText={getMiddleText}
          phoneIndicatives={phoneIndicatives}
          form={form}
          offerCode={offerCode}
        />
      </div>
    </FormProvider>
  );
};

SimulationRecapForm.propTypes = {
  getMiddleText: PropTypes.func,
  getMiddleBoolean: PropTypes.func,
  getSynthese: PropTypes.object,
  metadata: PropTypes.object,
  regimeObligatoire: PropTypes.object
};

export default SimulationRecapForm;
